import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
} from "reactstrap";
import { Link, useHistory, useParams, withRouter } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { withTranslation } from "react-i18next";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import Loader from "../../Components/Common/Loader";
import ProductImagesUploader from "./components/ProductImagesUploader";
import StockStatusBadge from "./components/StockStatusBadge";
import {
  PROMOTION_LOW_STOCK_THRESHOLD,
  STOCK_STATUS_META,
  previewStockStatus,
} from "../../constants/promotedProducts";
import {
  useCreatePromotedProduct,
  useDeleteProductImage,
  useProductCategories,
  usePromotedProduct,
  useSetProductImageCover,
  useUpdatePromotedProduct,
  useUploadProductImage,
} from "../../hooks/usePromotedProducts";
import { resolveApiErrorMessage } from "../../utils/resolveApiErrorMessage";

const validationSchema = Yup.object({
  designation: Yup.string().trim().required("La désignation est obligatoire"),
  price: Yup.number()
    .typeError("Le prix doit être un nombre")
    .positive("Le prix doit être supérieur à 0")
    .required("Le prix est obligatoire"),
  stock_quantity: Yup.number()
    .typeError("La quantité doit être un nombre")
    .integer("La quantité doit être un entier")
    .min(0, "La quantité ne peut pas être négative")
    .required("La quantité en stock est obligatoire"),
  seller_email: Yup.string().email("Adresse e-mail invalide"),
});

const ProductForm = (props) => {
  const { id } = useParams();
  const history = useHistory();
  const isEdit = Boolean(id);

  const categoriesQ = useProductCategories();
  const productQ = usePromotedProduct(id, { enabled: isEdit });

  const createMutation = useCreatePromotedProduct();
  const updateMutation = useUpdatePromotedProduct();
  const uploadMutation = useUploadProductImage();
  const deleteImageMutation = useDeleteProductImage();
  const setCoverMutation = useSetProductImageCover();

  const [pendingImages, setPendingImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Local key generator for not-yet-uploaded files (they have no server id).
  const nextUid = useRef(0);

  const categories = useMemo(() => {
    const data = categoriesQ.data;
    return Array.isArray(data) ? data : data?.results ?? [];
  }, [categoriesQ.data]);

  const product = productQ.data;
  const savedImages = product?.images ?? [];

  // Revoke object URLs so previews don't leak memory — on unmount only.
  // Revoking every time `pendingImages` changed blanked the tiles still on
  // screen (adding a second image killed the first one's preview);
  // handleRemovePending already revokes the single preview it drops.
  const pendingImagesRef = useRef(pendingImages);
  pendingImagesRef.current = pendingImages;
  useEffect(
    () => () => {
      pendingImagesRef.current.forEach((img) =>
        URL.revokeObjectURL(img.preview)
      );
    },
    []
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      designation: product?.designation ?? "",
      description: product?.description ?? "",
      category: product?.category?.id ?? "",
      brand: product?.brand ?? "",
      reference: product?.reference ?? "",
      price: product?.price ?? "",
      currency: product?.currency ?? "FCFA",
      unit: product?.unit ?? "",
      stock_quantity: product?.stock_quantity ?? 0,
      seller_name: product?.seller_name ?? "",
      seller_phone: product?.seller_phone ?? "",
      seller_email: product?.seller_email ?? "",
      location: product?.location ?? "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setSubmitting(true);

      // stock_status is deliberately NOT part of the payload: the server owns it.
      const payload = {
        designation: values.designation.trim(),
        description: values.description,
        category: values.category === "" ? null : Number(values.category),
        brand: values.brand,
        reference: values.reference,
        price: Number(values.price),
        currency: values.currency,
        unit: values.unit,
        stock_quantity: Number(values.stock_quantity),
        seller_name: values.seller_name,
        seller_phone: values.seller_phone,
        seller_email: values.seller_email,
        location: values.location,
      };

      try {
        const saved = isEdit
          ? await updateMutation.mutateAsync({ id, payload })
          : await createMutation.mutateAsync(payload);

        const productId = saved?.id ?? id;

        // Step 2: upload images one by one against the child endpoint.
        // The product already exists, so a failing image must not lose the entry.
        const failed = [];
        for (let index = 0; index < pendingImages.length; index += 1) {
          const img = pendingImages[index];
          try {
            await uploadMutation.mutateAsync({
              productId,
              file: img.file,
              isCover: img.isCover,
              position: savedImages.length + index,
              onUploadProgress: (event) => {
                if (!event.total) return;
                setUploadProgress((prev) => ({
                  ...prev,
                  [img.uid]: Math.round((event.loaded * 100) / event.total),
                }));
              },
            });
          } catch (err) {
            failed.push(img.file.name);
          }
        }

        if (failed.length > 0) {
          toast.error(
            `Produit enregistré, mais ${failed.length} image(s) n'ont pas pu être envoyées. Réessayez depuis le détail du produit.`,
            { autoClose: 6000 }
          );
        } else {
          toast.success(
            isEdit ? "Produit bien modifié" : "Produit bien enregistré",
            { autoClose: 3000 }
          );
        }

        setPendingImages([]);
        setUploadProgress({});
        history.push(`/promoted-products-details/${productId}`);
      } catch (err) {
        toast.error(
          resolveApiErrorMessage(err, "Le produit n'a pas pu être enregistré"),
          { autoClose: 5000 }
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleAddFiles = (files) => {
    setPendingImages((prev) => {
      const hasCover =
        prev.some((img) => img.isCover) ||
        savedImages.some((img) => img.is_cover);

      return [
        ...prev,
        ...files.map((file, index) => ({
          // eslint-disable-next-line no-plusplus
          uid: `local-${nextUid.current++}`,
          file,
          preview: URL.createObjectURL(file),
          // First image overall becomes the cover automatically.
          isCover: !hasCover && index === 0,
        })),
      ];
    });
  };

  const handleRemovePending = (target) => {
    setPendingImages((prev) => {
      URL.revokeObjectURL(target.preview);
      const next = prev.filter((img) => img.uid !== target.uid);
      // Never leave the batch cover-less when nothing is saved yet.
      if (target.isCover && next.length > 0 && savedImages.length === 0) {
        next[0] = { ...next[0], isCover: true };
      }
      return next;
    });
  };

  const handleSetCoverPending = (target) => {
    setPendingImages((prev) =>
      prev.map((img) => ({ ...img, isCover: img.uid === target.uid }))
    );
  };

  const handleRemoveSaved = (img) => {
    deleteImageMutation.mutate({ imageId: img.id, productId: id });
  };

  const handleSetCoverSaved = (img) => {
    setCoverMutation.mutate({ imageId: img.id, productId: id });
    setPendingImages((prev) => prev.map((p) => ({ ...p, isCover: false })));
  };

  const previewStatus = previewStockStatus(formik.values.stock_quantity);
  const previewMeta = STOCK_STATUS_META[previewStatus];

  document.title = isEdit
    ? props.t("Modifier un produit")
    : props.t("Nouveau produit");

  if (isEdit && productQ.isLoading) return <Loader />;
  if (isEdit && productQ.isError) {
    return <Loader error={resolveApiErrorMessage(productQ.error, "Erreur")} />;
  }

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} />
      <Container fluid>
        <BreadCrumb
          title={isEdit ? props.t("Modifier le produit") : props.t("Nouveau produit")}
          pageTitle={props.t("Promotion de produits")}
        />

        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col lg={8}>
              {/* ─── Bloc 1 : informations produit ─── */}
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">Informations produit</h5>
                </CardHeader>
                <CardBody>
                  <div className="mb-3">
                    <Label htmlFor="designation">
                      Désignation <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="designation"
                      name="designation"
                      type="text"
                      placeholder="Ex. Ciment CIMBENIN CPJ 35"
                      value={formik.values.designation}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={
                        formik.touched.designation && !!formik.errors.designation
                      }
                    />
                    <FormFeedback>{formik.errors.designation}</FormFeedback>
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      name="description"
                      type="textarea"
                      rows={4}
                      value={formik.values.description}
                      onChange={formik.handleChange}
                    />
                  </div>

                  <Row>
                    <Col md={4} className="mb-3">
                      <Label htmlFor="category">Catégorie</Label>
                      <Input
                        id="category"
                        name="category"
                        type="select"
                        value={formik.values.category}
                        onChange={formik.handleChange}
                      >
                        <option value="">Aucune</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.designation}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={4} className="mb-3">
                      <Label htmlFor="brand">Marque</Label>
                      <Input
                        id="brand"
                        name="brand"
                        type="text"
                        value={formik.values.brand}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={4} className="mb-3">
                      <Label htmlFor="reference">Référence</Label>
                      <Input
                        id="reference"
                        name="reference"
                        type="text"
                        value={formik.values.reference}
                        onChange={formik.handleChange}
                      />
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* ─── Bloc 2 : prix & stock ─── */}
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">Prix &amp; stock</h5>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={4} className="mb-3">
                      <Label htmlFor="price">
                        Prix <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="price"
                        name="price"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formik.values.price}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={formik.touched.price && !!formik.errors.price}
                      />
                      <FormFeedback>{formik.errors.price}</FormFeedback>
                    </Col>
                    <Col md={4} className="mb-3">
                      <Label htmlFor="currency">Devise</Label>
                      <Input
                        id="currency"
                        name="currency"
                        type="text"
                        value={formik.values.currency}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={4} className="mb-3">
                      <Label htmlFor="unit">Unité</Label>
                      <Input
                        id="unit"
                        name="unit"
                        type="text"
                        placeholder="sac, tonne, m³, unité…"
                        value={formik.values.unit}
                        onChange={formik.handleChange}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6} className="mb-3">
                      <Label htmlFor="stock_quantity">
                        Quantité en stock <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="stock_quantity"
                        name="stock_quantity"
                        type="number"
                        min="0"
                        step="1"
                        value={formik.values.stock_quantity}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.stock_quantity &&
                          !!formik.errors.stock_quantity
                        }
                      />
                      <FormFeedback>{formik.errors.stock_quantity}</FormFeedback>
                    </Col>

                    {/* Read-only preview: makes the derived rule visible to the
                        admin. Never submitted — the server owns the status. */}
                    <Col md={6} className="mb-3">
                      <Label>Statut du stock (calculé automatiquement)</Label>
                      <div className="form-control bg-light d-flex align-items-center">
                        <StockStatusBadge
                          status={previewStatus}
                          label={previewMeta?.label}
                        />
                      </div>
                      <small className="text-muted">
                        {Number(formik.values.stock_quantity) === 0
                          ? "Stock à 0 : le produit reste visible mais apparaît grisé sur mobile."
                          : Number(formik.values.stock_quantity) <=
                            PROMOTION_LOW_STOCK_THRESHOLD
                          ? `≤ ${PROMOTION_LOW_STOCK_THRESHOLD} unités — le badge sera orange sur mobile.`
                          : "Le badge sera vert sur mobile."}
                      </small>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* ─── Bloc 4 : vendeur ─── */}
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">Vendeur &amp; contact</h5>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={6} className="mb-3">
                      <Label htmlFor="seller_name">Nom du vendeur</Label>
                      <Input
                        id="seller_name"
                        name="seller_name"
                        type="text"
                        value={formik.values.seller_name}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6} className="mb-3">
                      <Label htmlFor="seller_phone">Téléphone</Label>
                      <Input
                        id="seller_phone"
                        name="seller_phone"
                        type="text"
                        placeholder="+229 97 00 00 00"
                        value={formik.values.seller_phone}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col md={6} className="mb-3">
                      <Label htmlFor="seller_email">E-mail</Label>
                      <Input
                        id="seller_email"
                        name="seller_email"
                        type="email"
                        value={formik.values.seller_email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={
                          formik.touched.seller_email &&
                          !!formik.errors.seller_email
                        }
                      />
                      <FormFeedback>{formik.errors.seller_email}</FormFeedback>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Label htmlFor="location">Localisation</Label>
                      <Input
                        id="location"
                        name="location"
                        type="text"
                        placeholder="Cotonou, Akpakpa"
                        value={formik.values.location}
                        onChange={formik.handleChange}
                      />
                    </Col>
                  </Row>
                  <small className="text-muted">
                    Au moins un moyen de contact est recommandé : le mobile
                    affiche un bouton d&apos;appel.
                  </small>
                </CardBody>
              </Card>
            </Col>

            {/* ─── Bloc 3 : images ─── */}
            <Col lg={4}>
              <Card>
                <CardBody>
                  <ProductImagesUploader
                    pendingImages={pendingImages}
                    savedImages={savedImages}
                    onAddFiles={handleAddFiles}
                    onRemovePending={handleRemovePending}
                    onRemoveSaved={handleRemoveSaved}
                    onSetCoverPending={handleSetCoverPending}
                    onSetCoverSaved={handleSetCoverSaved}
                    uploadProgress={uploadProgress}
                    disabled={submitting}
                  />
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <div className="alert alert-info mb-3" role="alert">
                    <i className="ri-information-line me-1" />
                    Un produit enregistré <b>n&apos;est pas encore visible</b>{" "}
                    sur l&apos;application mobile. Cliquez sur{" "}
                    <b>Valider</b> depuis le détail pour le publier.
                  </div>

                  <div className="d-flex gap-2">
                    <button
                      type="submit"
                      className="btn btn-primary w-100"
                      disabled={submitting}
                    >
                      {submitting
                        ? "Enregistrement…"
                        : isEdit
                        ? "Enregistrer les modifications"
                        : "Enregistrer le produit"}
                    </button>
                    <Link
                      to="/promoted-products"
                      className="btn btn-light"
                      tabIndex={submitting ? -1 : 0}
                    >
                      Annuler
                    </Link>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

ProductForm.propTypes = {
  t: PropTypes.any,
};

export default withRouter(withTranslation()(ProductForm));
